'use client'

import { useEffect, useState, useRef } from 'react'
import { useAuthStore } from '@/lib/stores/auth-store'
import { useThemeStore } from '@/lib/stores/theme-store'
import { httpClient } from '@/lib/api/http-client'

// Tipos para embed codes
interface EmbedCode {
  id: string
  owner_id: string
  name: string
  code_url: string // URL do arquivo no CDN
  code_type: 'script' | 'html'
  position: 'head' | 'body_start' | 'body_end'
  scope: 'global' | 'product'
  product_ids: string[]
  only_authenticated: boolean
  approved: boolean
  active: boolean
}

interface EmbedCodeProviderProps {
  productId?: string
}

/**
 * EmbedCodeGlobalProvider
 *
 * Carrega e injeta códigos embedados (scripts/widgets) configurados pelo mentor.
 * Scripts são carregados via <script src="...">.
 * HTML é carregado via fetch e injetado no DOM.
 * Respeita as regras de autenticação e escopo definidas.
 */
export function EmbedCodeGlobalProvider({ productId }: EmbedCodeProviderProps = {}) {
  const { isAuthenticated } = useAuthStore()
  const { currentTheme, isBlockStatusVerified } = useThemeStore()
  const [embedCodes, setEmbedCodes] = useState<EmbedCode[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const injectedIds = useRef<Set<string>>(new Set())

  const mentorId = currentTheme?.mentor_id
  const isMemberAreaBlocked = !isBlockStatusVerified || currentTheme?.member_area_blocked === true

  useEffect(() => {
    if (!isMemberAreaBlocked) return

    injectedIds.current.forEach((id) => {
      const element = document.getElementById(`embed-code-${id}`)
      if (element) {
        element.remove()
      }
    })
    injectedIds.current.clear()
    setEmbedCodes([])
    setIsLoaded(false)
  }, [isMemberAreaBlocked])

  // Buscar códigos embedados quando mentorId estiver disponível
  useEffect(() => {
    if (!mentorId || isMemberAreaBlocked) return

    const fetchEmbedCodes = async () => {
      try {
        const params = new URLSearchParams()
        if (productId) params.append('productId', productId)
        params.append('isAuthenticated', String(isAuthenticated))

        const response = await httpClient.get<EmbedCode[]>(
          `/integrations/embed-codes/active/${mentorId}?${params.toString()}`
        )
        setEmbedCodes(response.data || [])
        setIsLoaded(true)
      } catch (error) {
        console.error('[EmbedCodeProvider] Erro ao buscar embed codes:', error)
        setIsLoaded(true)
      }
    }

    fetchEmbedCodes()
  }, [mentorId, productId, isAuthenticated, isMemberAreaBlocked])

  // Injetar códigos no DOM
  useEffect(() => {
    if (!isLoaded || embedCodes.length === 0 || isMemberAreaBlocked) return

    embedCodes.forEach((embedCode) => {
      // Evitar injeção duplicada
      if (injectedIds.current.has(embedCode.id)) return

      injectCode(embedCode)
        .then(async () => {
          injectedIds.current.add(embedCode.id)
          // Analytics: embed renderizado com sucesso (Umami)
          try {
            const { trackEvent } = await import('@/lib/analytics')
            trackEvent('embed_opened', {
              widget_type: (embedCode as any)?.type || (embedCode as any)?.name,
            })
          } catch {
            /* no-op */
          }
        })
        .catch((error) => {
          console.error(`[EmbedCodeProvider] Erro ao injetar código "${embedCode.name}":`, error)
        })
    })

    // Cleanup: remover códigos quando componente desmontar
    return () => {
      injectedIds.current.forEach((id) => {
        const element = document.getElementById(`embed-code-${id}`)
        if (element) {
          element.remove()
        }
      })
      injectedIds.current.clear()
    }
  }, [embedCodes, isLoaded, isMemberAreaBlocked])

  return null
}

/**
 * Injeta o código no DOM na posição especificada.
 * Para scripts: usa <script src="url"> para melhor performance e cache.
 * Para HTML: faz fetch do conteúdo e injeta no DOM.
 */
async function injectCode(embedCode: EmbedCode) {
  const containerId = `embed-code-${embedCode.id}`

  // Verificar se já existe
  if (document.getElementById(containerId)) return

  // Criar container
  const container = document.createElement('div')
  container.id = containerId
  container.setAttribute('data-embed-name', embedCode.name)
  container.setAttribute('data-embed-type', embedCode.code_type)

  if (embedCode.code_type === 'script') {
    // Para scripts: criar <script src="url"> para melhor cache do CDN
    const script = document.createElement('script')
    script.src = embedCode.code_url
    script.async = true
    script.onload = () => {
      console.log(`[EmbedCodeProvider] Script "${embedCode.name}" carregado com sucesso`)
    }
    script.onerror = (error) => {
      console.error(`[EmbedCodeProvider] Erro ao carregar script "${embedCode.name}":`, error)
    }
    container.appendChild(script)
  } else {
    // Para HTML: fazer fetch do conteúdo e injetar
    try {
      const response = await fetch(embedCode.code_url)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      const htmlContent = await response.text()
      container.innerHTML = htmlContent
    } catch (error) {
      console.error(`[EmbedCodeProvider] Erro ao carregar HTML de ${embedCode.code_url}:`, error)
      return // Não inserir no DOM se falhar
    }
  }

  // Inserir na posição correta
  switch (embedCode.position) {
    case 'head':
      document.head.appendChild(container)
      break
    case 'body_start':
      if (document.body.firstChild) {
        document.body.insertBefore(container, document.body.firstChild)
      } else {
        document.body.appendChild(container)
      }
      break
    case 'body_end':
    default:
      document.body.appendChild(container)
      break
  }
}
